import * as React from 'react'
import { theme } from '../theme'
import { useImageEditor } from './useImageEditor'
import { useDisclaimerText } from '../disclaimer_texts/useDisclaimerText'
import { aspectOptions, DimensionVariantImage } from '../dimension_management/DimensionOptions'
import { useDimensionOptions } from '../dimension_management/useDimensionOptions'
import { useImageTransform } from '../image_transform/useImageTransform'
import { useSaveImage, SavedImage } from './useSaveImage'
import { useAiGeneration } from '../generate_with_ai/useAiGeneration'
import { LoadingProvider, useLoadingContext } from './loading_state/LoadingContext'
import { UnsavedChangesDialog } from './unsaved_dialogue/UnsavedChangesDialog'
import { useUnsavedChangesDialog } from './unsaved_dialogue/useUnsavedChangesDialog'
import { useUnsavedChanges } from './useUnsavedChanges'
import { useImageUpload } from './useImageUpload'
import { LeftPanel } from './components/LeftPanel'
import { ActionSelector } from './components/ActionSelector'
import { CloseButton } from './components/CloseButton'
import { RightControlPanel } from './components/RightControlPanel'
import { ContentArea } from './components/ContentArea'
import { ModalDimensionsProvider, useModalDimensions } from './components/ModalDimensionsProvider'

// AI placeholder image path
const aiPlaceholderImage = '/images/create_with_ai_placeholder_1.jpeg'

export interface Props {
	isOpen: boolean
	onClose: () => void
	imageSrc?: string
	onAccept?: (result: { url: string; width: number; height: number; cleanup: () => void }) => void
	onMaskChange?: (blob: Blob) => void
	onPromptChange?: (prompt: string) => void
}

export const CustomEditorModal: React.FC<Props> = (props) => {
	return (
		<LoadingProvider>
			<ModalDimensionsProvider>
				<CustomEditorModalContent {...props} />
			</ModalDimensionsProvider>
		</LoadingProvider>
	)
}

const CustomEditorModalContent: React.FC<Props> = ({
	isOpen,
	onClose,
	imageSrc,
	onMaskChange,
	onAccept,
	onPromptChange,
}) => {
	// Get modal dimensions from the provider
	const { modalDimensions, getScaledDimensions } = useModalDimensions()

	const [selectedAction, setSelectedAction] = React.useState<string>('create')

	// State for the current image being edited
	const [currentImage, setCurrentImage] = React.useState<SavedImage | null>(null)

	// Use dimension options hook
	const {
		selectedDimension,
		dimensionImages,
		handleDimensionSelect,
		getCurrentDimensionImage,
		updateDimensionImage,
		initializeDimensionImages,
		resetDimensionImages,
		setDimensionImages,
		registerImageRef,
		registerContainerRef,
		areAllAspectRatiosValid,
	} = useDimensionOptions({
		initialDimension: aspectOptions[0],
		initialImages: currentImage?.dimensionVariants ?? {},
	})

	// Set default aspect ratio to 1.91:1 when adjust tool is selected
	React.useEffect(() => {
		if (selectedAction === 'adjust') {
			// aspectOptions[0] is the 1.91:1 ratio
			handleDimensionSelect(aspectOptions[0])
		}
	}, [selectedAction, handleDimensionSelect])

	// Replace useImageHistory section with:
	const [savedImages, setSavedImages] = React.useState<SavedImage[]>([])

	// Add a ref to track if images have been manually deleted
	const imagesManuallyDeleted = React.useRef(false)

	// Add state to track if the current image has been modified
	const [hasModifiedImage, setHasModifiedImage] = React.useState(false)

	// Modify updateCurrentImage to use the hook's updateDimensionImage
	const updateCurrentImage = React.useCallback(
		(imageData: { url: string; width: number; height: number; dimensionId?: string }) => {
			// Get the dimension ID from the imageData or use the currently selected dimension
			// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
			const targetDimensionId = imageData.dimensionId || selectedDimension.id

			// Use the hook's updateDimensionImage function
			updateDimensionImage(targetDimensionId, imageData)

			// Update the current image with the new data, but preserve its id and timestamp
			// This ensures we're not creating a new image entry
			if (currentImage) {
				setCurrentImage({
					...currentImage,
					// Only update the URL, width, and height if this is the main dimension
					// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
					...(targetDimensionId === (currentImage.mainDimensionId || 'option1')
						? {
								url: imageData.url,
								width: imageData.width,
								height: imageData.height,
						  }
						: {}),
					// Preserve the dimensionVariants and mainDimensionId
					dimensionVariants: {
						...(currentImage.dimensionVariants ?? {}),
						[targetDimensionId]: {
							url: imageData.url,
							width: imageData.width,
							height: imageData.height,
						},
					},
				})
			} else {
				// If there's no current image, create a new one
				const timestamp = Date.now()
				const newImage: SavedImage = {
					...imageData,
					timestamp,
					id: 'current',
					dimensionVariants: {
						[targetDimensionId]: {
							url: imageData.url,
							width: imageData.width,
							height: imageData.height,
						},
					},
					mainDimensionId: 'option1', // Default to 1.91:1 as main
				}
				setCurrentImage(newImage)
			}

			// We don't need to set hasModifiedImage here since it's set in the specific callbacks
			// that use updateCurrentImage (like onAccept, onOutpaintComplete, etc.)
		},
		[selectedDimension.id, currentImage, updateDimensionImage],
	)

	const { saveImage } = useSaveImage({
		onSave: (image) => {
			setSavedImages((prev) => [image, ...prev])
			setCurrentImage(image)

			// Update all dimension variants in the dimensionImages state
			if (image.dimensionVariants) {
				// Update the entire dimensionImages state with all variants from the saved image
				setDimensionImages(image.dimensionVariants)
			} else {
				// If no dimension variants, at least update the current dimension
				updateDimensionImage(selectedDimension.id, {
					url: image.url,
					width: image.width,
					height: image.height,
				})
			}
		},
	})

	// Initialize with initial image if provided - use the hook's initializeDimensionImages
	React.useEffect(() => {
		// Only load the initial image if no images exist and none have been manually deleted
		if (imageSrc && !savedImages.length && !imagesManuallyDeleted.current) {
			const img = new Image()
			img.onload = () => {
				const savedImage = saveImage({
					url: imageSrc,
					width: img.width,
					height: img.height,
				})

				// Use the hook's initializeDimensionImages function
				initializeDimensionImages({
					url: savedImage.url,
					width: savedImage.width,
					height: savedImage.height,
				})
			}

			img.src = imageSrc
		}
	}, [imageSrc, savedImages.length, saveImage, initializeDimensionImages])

	// Core state and logic from useImageEditor hook
	const {
		imageCanvasRef,
		maskCanvasRef,
		prompt,
		showPromptInput,
		showInputAbove,
		startDrawing,
		stopDrawing,
		draw,
		setPrompt,
		hasMask,
		currentTool,
		brushSize,
		setBrushSize,
		setCurrentTool,
		undo,
		handleSubmit,
		imageWidth,
		imageHeight,
		setDimensions,
		clearMask,
	} = useImageEditor({
		imageSrc:
			selectedAction === 'create' && !currentImage?.url
				? aiPlaceholderImage
				: // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
				  getCurrentDimensionImage()?.url || currentImage?.url || imageSrc || aiPlaceholderImage,
		onAccept: (result) => {
			updateCurrentImage(result)
			// Mark that the image has been modified when handleSubmit completes
			setHasModifiedImage(true)
		},
		onMaskChange,
		onError: (error) => {
			// Simple error handling - could be enhanced with a proper error notification system
			if (error instanceof Error) {
				// You could integrate this with your notification system later
			}
		},
		dimensionId: selectedDimension.id,
	})

	// Update canvas when dimension selection changes
	React.useEffect(() => {
		const dimensionImage = dimensionImages[selectedDimension.id]
		if (dimensionImage) {
			const img = new Image()
			img.onload = () => {
				const canvas = imageCanvasRef.current
				if (canvas) {
					const ctx = canvas.getContext('2d')
					if (ctx) {
						// Update canvas dimensions
						canvas.width = dimensionImage.width
						canvas.height = dimensionImage.height

						// Clear and draw new image
						ctx.clearRect(0, 0, canvas.width, canvas.height)
						ctx.drawImage(img, 0, 0, dimensionImage.width, dimensionImage.height)

						// Update dimensions state
						setDimensions(dimensionImage.width, dimensionImage.height)
					}
				}
			}
			img.src = dimensionImage.url
		}
	}, [selectedDimension.id, dimensionImages, imageCanvasRef, setDimensions])

	// Initialize AI placeholder image dimensions when switching to create mode
	React.useEffect(() => {
		if (selectedAction === 'create' && !currentImage?.url) {
			const img = new Image()
			img.onload = () => {
				setDimensions(img.width, img.height)
			}
			img.src = aiPlaceholderImage
		}
	}, [selectedAction, currentImage?.url, setDimensions])

	// Modify handleImageSelect to use the hook's setDimensionImages
	const handleImageSelect = React.useCallback(
		(image: SavedImage) => {
			setCurrentImage(image)
			setDimensions(image.width, image.height)

			// If the image has dimension variants, load them using the hook's setDimensionImages
			if (image.dimensionVariants) {
				setDimensionImages(image.dimensionVariants)
			} else {
				// Otherwise, initialize all dimension variants with the selected image
				initializeDimensionImages({
					url: image.url,
					width: image.width,
					height: image.height,
				})
			}

			// Reset the modified flag when selecting a new image
			setHasModifiedImage(false)
		},
		[setDimensions, setDimensionImages, initializeDimensionImages],
	)

	// Add a new state to track the pending image selection
	const [pendingImageSelection, setPendingImageSelection] = React.useState<SavedImage | null>(null)

	// Create a wrapped version of handleImageSelect that checks for unsaved changes
	const handleImageSelectWithDialog = React.useCallback(
		(image: SavedImage) => {
			if (hasModifiedImage) {
				// If there are unsaved changes, store the pending selection and show the dialog
				setPendingImageSelection(image)
				setIsDialogOpen(true)
				setPendingAction('selectImage')
			} else {
				// If no unsaved changes, just select the image directly
				handleImageSelect(image)
			}
		},
		[hasModifiedImage, handleImageSelect],
	)

	// Disclaimer text state and rendering logic
	const {
		text,
		setText,
		previewRef,
		handleAccept: handleDisclaimerAccept,
		textConfig,
		textAlignment,
		setTextAlignment,
		increaseScale,
		decreaseScale,
	} = useDisclaimerText({
		imageWidth,
		imageHeight,
		onAccept: (result) => {
			updateCurrentImage(result)
			// Mark that the image has been modified when disclaimer text is applied
			setHasModifiedImage(true)
		},
		imageCanvasRef,
		dimensionImages,
		onMultiDimensionAccept: (results) => {
			// Update all dimension variants with the new images
			Object.entries(results).forEach(([dimensionId, result]) => {
				updateCurrentImage({
					...result,
					dimensionId,
				})
			})
			// Mark that the image has been modified
			setHasModifiedImage(true)
		},
	})

	// Custom text change handler to automatically preview on all dimensions
	const handleDisclaimerTextChange = React.useCallback(
		(newText: string) => {
			setText(newText)
			// The preview is automatically handled by the useDisclaimerText hook
		},
		[setText],
	)

	// Handler for text alignment changes
	const handleTextAlignmentChange = React.useCallback(
		(alignment: 'left' | 'center') => {
			setTextAlignment(alignment)
		},
		[setTextAlignment],
	)

	// Add useImageTransform hook
	const {
		transform,
		containerRef,
		handleMouseDown,
		handleMouseMove,
		handleMouseUp,
		emptySpace,
		setTransform,
		handleOutpaint,
	} = useImageTransform({
		containerWidth: getScaledDimensions(selectedDimension.displayWidth, selectedDimension.displayHeight).width,
		containerHeight: getScaledDimensions(selectedDimension.displayWidth, selectedDimension.displayHeight).height,
		imageWidth,
		imageHeight,
		selectedDimension: {
			targetWidth: selectedDimension.targetWidth,
			targetHeight: selectedDimension.targetHeight,
		},
		onOutpaintStart: () => {
			// No need to set loading state here as it's managed by the hook
		},
		onOutpaintComplete: (result) => {
			// Load the new image into the canvas
			const img = new Image()
			img.onload = () => {
				const canvas = imageCanvasRef.current
				if (canvas) {
					const ctx = canvas.getContext('2d')
					if (ctx) {
						// Update canvas dimensions
						canvas.width = result.width
						canvas.height = result.height

						// Clear and draw new image
						ctx.clearRect(0, 0, canvas.width, canvas.height)
						ctx.drawImage(img, 0, 0, result.width, result.height)

						// Update image dimensions state
						setDimensions(result.width, result.height)

						// Only update the current dimension variant, not all variants
						// This ensures that adjustments to one dimension don't affect others
						setDimensionImages((prev) => ({
							...prev,
							[selectedDimension.id]: {
								url: result.url,
								width: result.width,
								height: result.height,
							},
						}))

						// Update the current image with the new data, but preserve its id and timestamp
						if (currentImage) {
							setCurrentImage({
								...currentImage,
								// Only update the URL, width, and height if this is the main dimension
								// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
								...(selectedDimension.id === (currentImage.mainDimensionId || 'option1')
									? {
											url: result.url,
											width: result.width,
											height: result.height,
									  }
									: {}),
								// Update the dimension variants
								dimensionVariants: {
									// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
									...(currentImage.dimensionVariants || {}),
									[selectedDimension.id]: {
										url: result.url,
										width: result.width,
										height: result.height,
									},
								},
							})
						}

						// Mark that the image has been modified when outpainting is completed
						setHasModifiedImage(true)

						// Explicitly reset transform state to ensure UI is updated
						setTransform({
							scale: 1,
							position: { x: 0, y: 0 },
							isDragging: false,
						})
						// Don't automatically change the action
					}
				}
			}
			img.src = result.url
		},
		onOutpaintError: () => {
			// Error handling is done by the hook
		},
	})

	// Handle prompt text changes and notify parent
	const handlePromptChange = React.useCallback(
		(value: string) => {
			setPrompt(value)
			onPromptChange?.(value)
		},
		[setPrompt, onPromptChange],
	)

	// Handle create new with AI click
	const handleCreateNew = React.useCallback(() => {
		setSelectedAction('create')
	}, [])

	// Modify handleDeleteImage to use the hook's resetDimensionImages
	const handleDeleteImage = React.useCallback(
		(imageId: string) => {
			// Mark that images have been manually deleted
			imagesManuallyDeleted.current = true
			// Remove the image from savedImages
			const updatedImages = savedImages.filter((img) => img.id !== imageId)
			setSavedImages(updatedImages)
			// If the deleted image is the current image, select the first available image or reset
			if (currentImage?.id === imageId) {
				const nextImage = updatedImages.find((img) => img.id !== imageId)
				if (nextImage) {
					setCurrentImage(nextImage)
					setDimensions(nextImage.width, nextImage.height)

					// If the next image has dimension variants, load them
					if (nextImage.dimensionVariants) {
						setDimensionImages(nextImage.dimensionVariants)
					} else {
						// Otherwise, initialize all dimension variants with the selected image
						initializeDimensionImages({
							url: nextImage.url,
							width: nextImage.width,
							height: nextImage.height,
						})
					}
				} else {
					// If no images left, reset to default state
					setCurrentImage(null)

					// If we're in create mode, use the AI placeholder image
					if (selectedAction === 'create') {
						const img = new Image()
						img.onload = () => {
							setDimensions(img.width, img.height)
						}
						img.src = aiPlaceholderImage
					}

					// Reset dimension images using the hook's resetDimensionImages
					resetDimensionImages()
					// If we're in create mode, don't change the action
					if (selectedAction !== 'create') {
						// Reset to adjust mode when all images are deleted
						setSelectedAction('adjust')
					}
				}
			}
		},
		[
			currentImage,
			savedImages,
			setDimensions,
			selectedAction,
			setDimensionImages,
			initializeDimensionImages,
			resetDimensionImages,
		],
	)

	// Modify handleSaveAsNew to use the hook's dimensionImages
	const handleSaveAsNew = React.useCallback(() => {
		const canvas = imageCanvasRef.current
		if (canvas) {
			const dataUrl = canvas.toDataURL('image/png')

			// Get the current dimension variants from the hook
			const currentVariants = { ...dimensionImages }

			// Ensure the current dimension is up to date
			updateDimensionImage(selectedDimension.id, {
				url: dataUrl,
				width: canvas.width,
				height: canvas.height,
			})

			// Update currentVariants with the latest image
			currentVariants[selectedDimension.id] = {
				url: dataUrl,
				width: canvas.width,
				height: canvas.height,
			}

			// Generate a unique timestamp-based ID for this new collection
			const timestamp = Date.now()
			const uniqueId = `saved-${timestamp}-${Math.floor(Math.random() * 1000)}`

			// Save the image with all dimension variants and a unique ID
			const savedImageResult = saveImage({
				// Use the main image (1.91:1) as the primary image data if available
				url: currentVariants['option1']?.url ?? dataUrl,
				width: currentVariants['option1']?.width ?? canvas.width,
				height: currentVariants['option1']?.height ?? canvas.height,
				dimensionVariants: currentVariants,
				mainDimensionId: 'option1', // option1 is the 1.91:1 ratio (main image)
				id: uniqueId, // Explicitly set a unique ID
			})

			// Update the dimensionImages state with the saved image's dimension variants
			// This ensures the DimensionOptions component renders the correct URLs immediately
			if (savedImageResult.dimensionVariants) {
				setDimensionImages(savedImageResult.dimensionVariants)
			}

			// Call onAccept with the new image data
			onAccept?.({
				url: dataUrl,
				width: canvas.width,
				height: canvas.height,
				cleanup: () => {
					// Add any cleanup logic if needed
				},
			})

			// Reset the modified flag since we've saved the changes
			setHasModifiedImage(false)
		}
	}, [saveImage, onAccept, dimensionImages, selectedDimension.id, updateDimensionImage, setDimensionImages])

	// Add state for AI generation inputs
	const {
		articleUrls,
		customPrompt,
		referenceImage,
		error: aiError,
		generationType,
		selectedDimension: aiSelectedDimension,
		isGenerating,
		handleArticleUrlsChange,
		handleCustomPromptChange,
		handleReferenceImageSelect,
		handleGenerationTypeChange,
		handleDimensionChange: handleAiDimensionChange,
		clearError,
		reset,
		handleGenerate,
		generationMode,
		onGenerationModeChange,
		isImageSuitableForStructure,
		referenceImageDimensions,
	} = useAiGeneration({
		onGenerateSuccess: (result) => {
			// When generating a new image, we want to create a new dimension collection
			// First, create a new dimension variants object with the generated image
			const newDimensionVariants: Record<string, DimensionVariantImage> = {}

			// Set the generated image for the current AI dimension
			const generatedDimensionId = aiSelectedDimension?.id ?? 'option1'
			newDimensionVariants[generatedDimensionId] = {
				url: result.url,
				width: result.width,
				height: result.height,
			}

			// For other dimensions, initialize with the same image
			// Later, users can edit each dimension individually
			aspectOptions.forEach((option) => {
				if (option.id !== generatedDimensionId) {
					newDimensionVariants[option.id] = {
						url: result.url,
						width: result.width,
						height: result.height,
					}
				}
			})

			// Update dimension images state using the hook's setDimensionImages
			setDimensionImages(newDimensionVariants)

			// Create a new current image with all dimension variants
			const timestamp = Date.now()
			const uniqueId = `generated-${timestamp}-${Math.floor(Math.random() * 1000)}`

			// Create a new image with a unique ID
			const newImage: SavedImage = {
				...result,
				timestamp,
				id: uniqueId,
				dimensionVariants: newDimensionVariants,
				mainDimensionId: 'option1', // Default to 1.91:1 as main
			}

			// Update current image and add to saved images
			setCurrentImage(newImage)
			setSavedImages((prev) => [newImage, ...prev])
		},
		onGenerateError: () => {
			// Error is handled by the hook internally
		},
	})

	// Use loading context for all loading states
	const { isLoading, getCurrentMessage } = useLoadingContext()

	// Update the useUnsavedChangesDialog hook to use hasModifiedImage
	const {
		isDialogOpen,
		setIsDialogOpen,
		handleClose,
		handleActionChange,
		handleGenerate: handleGenerateWithDialog,
		closeDialog,
		handleSaveChanges,
		handleDiscardChanges,
		pendingAction,
		setPendingAction,
	} = useUnsavedChangesDialog({
		// Use our hasModifiedImage state to determine if there are unsaved changes
		hasUnsavedChanges: () => hasModifiedImage,
		onSaveImage: handleSaveAsNew,
		onClose,
		onActionChange: (action) => {
			// Clear any existing prompt when changing actions
			setPrompt('')

			// Clear disclaimer text when switching away from disclaimer tool
			if (selectedAction === 'disclaimer' && action !== 'disclaimer') {
				setText('')

				// Force a canvas clear by accessing the preview ref
				if (previewRef.current) {
					const ctx = previewRef.current.getContext('2d')
					if (ctx) {
						ctx.clearRect(0, 0, imageWidth, imageHeight)
					}
				}
			}

			setSelectedAction(action)

			// Reset tool when changing actions
			setCurrentTool(null)

			// Reset mask when changing away from add/erase
			if ((selectedAction === 'add' || selectedAction === 'erase') && action !== 'add' && action !== 'erase') {
				clearMask()
			}
		},
		onGenerate: handleGenerate,
		// Add a function to reset the modified flag when changes are discarded
		resetModifiedFlag: () => setHasModifiedImage(false),
	})

	// Use the extracted useUnsavedChanges hook
	const { extendedHandleSaveChanges, extendedHandleDiscardChanges, wrappedHandleGenerate } = useUnsavedChanges({
		pendingAction,
		pendingImageSelection,
		setPendingImageSelection,
		handleSaveChanges,
		handleDiscardChanges,
		handleImageSelect,
		handleGenerateWithDialog,
	})

	// Use the extracted useImageUpload hook
	const { handleImageUpload } = useImageUpload({
		saveImage,
		initializeDimensionImages,
	})

	if (!isOpen) return null

	return (
		<div
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: 'rgba(0, 0, 0, 0.5)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				zIndex: 9999,
				padding: theme.spacing.md,
			}}
		>
			{/* Unsaved Changes Dialog */}
			<UnsavedChangesDialog
				isOpen={isDialogOpen}
				onClose={closeDialog}
				onSave={extendedHandleSaveChanges}
				onDiscard={extendedHandleDiscardChanges}
			/>

			{/* Using LeftPanel component for thumbnails */}
			<LeftPanel
				width={modalDimensions.sidebarWidth}
				height={modalDimensions.height}
				savedImages={savedImages}
				currentImage={currentImage}
				onImageSelect={handleImageSelectWithDialog}
				onCreateNew={handleCreateNew}
				onDeleteImage={handleDeleteImage}
				onImageUpload={handleImageUpload}
			/>

			{/* Right Box - Main Editor and Tools */}
			<div
				style={{
					backgroundColor: theme.colors.background,
					borderRadius: theme.borderRadius.md,
					width: `calc(${modalDimensions.width} - ${modalDimensions.sidebarWidth} - 20px)`, // Adjust width accounting for gap
					height: modalDimensions.height,
					display: 'flex',
					position: 'relative',
					overflow: 'hidden',
					zIndex: 10000,
					flexDirection: 'column',
				}}
			>
				{/* Using CloseButton component */}
				<CloseButton onClick={handleClose} />

				{/* Using ActionSelector component */}
				<ActionSelector selectedAction={selectedAction} onActionChange={handleActionChange} />

				{/* Main content and tools */}
				<div
					style={{
						display: 'flex',
						flex: 1,
						overflow: 'hidden',
					}}
				>
					{/* Content area with image and dimension options */}
					<ContentArea
						containerRef={containerRef}
						selectedAction={selectedAction}
						imageCanvasRef={imageCanvasRef}
						maskCanvasRef={maskCanvasRef}
						previewRef={previewRef}
						transform={transform}
						currentTool={currentTool}
						brushSize={brushSize}
						handleMouseDown={handleMouseDown}
						handleMouseMove={handleMouseMove}
						handleMouseUp={handleMouseUp}
						startDrawing={startDrawing}
						draw={draw}
						stopDrawing={stopDrawing}
						showPromptInput={showPromptInput}
						showInputAbove={showInputAbove}
						prompt={prompt}
						onPromptChange={handlePromptChange}
						currentImage={currentImage ?? undefined}
						imageSrc={imageSrc}
						placeholderImage={aiPlaceholderImage}
						text={text}
						imageWidth={imageWidth}
						imageHeight={imageHeight}
						textConfig={textConfig}
						textAlignment={textAlignment}
						isLoading={isLoading}
						getCurrentMessage={getCurrentMessage}
						getScaledDimensions={getScaledDimensions}
						onSetSelectedAction={setSelectedAction}
						onImageUpload={handleImageUpload}
						selectedDimension={selectedDimension}
						aiSelectedDimension={aiSelectedDimension}
						dimensionImages={dimensionImages}
						handleDimensionSelect={handleDimensionSelect}
						handleAiDimensionChange={handleAiDimensionChange}
						registerImageRef={registerImageRef}
						registerContainerRef={registerContainerRef}
					/>

					{/* Using RightControlPanel component */}
					<RightControlPanel
						selectedAction={selectedAction}
						controlsWidth={modalDimensions.controlsWidth}
						// Adjust tool props
						selectedDimension={selectedDimension}
						transform={transform}
						emptySpace={emptySpace}
						imageWidth={imageWidth}
						imageHeight={imageHeight}
						onResetTransform={() => {
							setTransform({
								scale: 1,
								position: { x: 0, y: 0 },
								isDragging: false,
							})
						}}
						onAcceptTransform={() => {
							const imageBase64 = imageCanvasRef.current?.toDataURL('image/png')
							if (imageBase64) {
								handleOutpaint(imageBase64)
							}
						}}
						getScaledDimensions={getScaledDimensions}
						setTransform={setTransform}
						// Create with AI props
						articleUrls={articleUrls}
						customPrompt={customPrompt}
						referenceImage={referenceImage}
						aiError={aiError}
						generationType={generationType}
						aiSelectedDimension={aiSelectedDimension}
						isGenerating={isGenerating}
						handleArticleUrlsChange={handleArticleUrlsChange}
						handleCustomPromptChange={handleCustomPromptChange}
						handleReferenceImageSelect={handleReferenceImageSelect}
						handleGenerationTypeChange={handleGenerationTypeChange}
						handleAiDimensionChange={handleAiDimensionChange}
						handleGenerate={wrappedHandleGenerate}
						clearError={clearError}
						reset={reset}
						generationMode={generationMode}
						onGenerationModeChange={onGenerationModeChange}
						isImageSuitableForStructure={isImageSuitableForStructure}
						referenceImageDimensions={referenceImageDimensions}
						registerImageRef={registerImageRef}
						registerContainerRef={registerContainerRef}
						// Tool selector props
						currentTool={currentTool}
						isEditing={true}
						onToolSelect={setCurrentTool}
						hasMask={hasMask}
						currentPrompt={prompt}
						onUndo={undo}
						onAccept={handleSubmit}
						brushSize={brushSize}
						onBrushSizeChange={setBrushSize}
						disclaimerText={text}
						onDisclaimerTextChange={handleDisclaimerTextChange}
						onDisclaimerAccept={handleDisclaimerAccept}
						isProcessing={isLoading()}
						areAllAspectRatiosValid={areAllAspectRatiosValid()}
						onSwitchToAdjustTool={() => setSelectedAction('adjust')}
						textAlignment={textAlignment}
						onTextAlignmentChange={handleTextAlignmentChange}
						onIncreaseFontSize={increaseScale}
						onDecreaseFontSize={decreaseScale}
						// Save button props
						onSaveAsNew={handleSaveAsNew}
						isLoading={isLoading()}
					/>
				</div>
			</div>
		</div>
	)
}
